import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, CheckCircle2, Shield, Clock, ThumbsUp, Wrench, Phone } from 'lucide-react';
import { QuoteForm } from '../components/QuoteForm';
import { FAQAccordion } from '../components/FAQAccordion';
// Mock database for service content
const serviceData: Record<string, any> = {
  roofing: {
    title: 'Professional Roofing & Repairs',
    image: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?w=1920&q=80',
    description: "Your roof is your home's first line of defense against Michigan's harsh weather. At Moceri Residential Services, we specialize in complete roof replacements, tear-offs, and emergency repairs using premium architectural shingles designed to withstand high winds and heavy snow.",
    benefits: [{
      title: 'Premium Materials',
      desc: 'We install top-tier architectural shingles from trusted brands like GAF and Owens Corning.',
      icon: Shield
    }, {
      title: 'Expert Installation',
      desc: 'Our certified crews follow strict manufacturer guidelines to ensure your warranty remains valid.',
      icon: Wrench
    }, {
      title: 'Thorough Cleanup',
      desc: 'We use magnetic sweepers to ensure not a single nail is left in your driveway or lawn.',
      icon: ThumbsUp
    }],
    process: ['Comprehensive Inspection & Estimate', 'Material Selection & Scheduling', 'Complete Tear-Off & Deck Inspection', 'Underlayment & Shingle Installation', 'Final Inspection & Cleanup'],
    faqs: [{
      question: 'How do I know if I need a new roof or just repairs?',
      answer: 'Signs you need a replacement include curling or missing shingles, excessive granule loss in gutters, sagging roof deck, or water stains on your interior ceilings. We provide honest assessments and will never sell you a replacement if a repair will suffice.'
    }, {
      question: 'How long does a roof replacement take?',
      answer: 'Most residential roof replacements in Macomb and St. Clair counties are completed in 1 to 2 days, depending on the size and complexity of the roof.'
    }, {
      question: 'Do you offer warranties?',
      answer: "Yes, we offer comprehensive workmanship warranties alongside the manufacturer's material warranties, which can last up to 50 years depending on the shingle chosen."
    }]
  },
  gutters: {
    title: 'Seamless Gutter Services',
    image: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1920&q=80',
    description: 'Properly functioning gutters are essential for directing water away from your foundation. We offer custom-fit seamless aluminum gutters, professional cleaning, and advanced gutter protection systems to keep leaves and debris out permanently.',
    benefits: [{
      title: 'Seamless Design',
      desc: 'Custom cut on-site to perfectly fit your home, eliminating leaks at seams.',
      icon: Shield
    }, {
      title: 'Heavy-Duty Hangers',
      desc: 'We use hidden hangers spaced closely together to withstand heavy Michigan ice and snow loads.',
      icon: Wrench
    }, {
      title: 'Gutter Guards',
      desc: 'Optional premium leaf protection systems so you never have to climb a ladder again.',
      icon: ThumbsUp
    }],
    process: ['Property Assessment & Measurement', 'Color Selection to Match Trim', 'On-Site Custom Fabrication', 'Pitch Calculation & Installation', 'Downspout Placement & Testing'],
    faqs: [{
      question: 'Why are seamless gutters better than sectional?',
      answer: 'Seamless gutters are formed from a single piece of aluminum, meaning there are no joints or seams along the length of the gutter. This drastically reduces the chance of leaks and improves the overall structural integrity.'
    }, {
      question: 'Do gutter guards really work?',
      answer: 'Yes, high-quality gutter guards prevent leaves, pine needles, and debris from entering the gutter while allowing water to flow freely. This prevents clogs and protects your foundation.'
    }, {
      question: "How often should gutters be cleaned if I don't have guards?",
      answer: 'In Michigan, we recommend cleaning your gutters at least twice a year: once in late spring after trees drop seeds/blossoms, and once in late fall after the leaves have fallen.'
    }]
  },
  siding: {
    title: 'Professional Siding Installation',
    image: 'https://images.unsplash.com/photo-1480074568708-e7b720bb3f09?w=1920&q=80',
    description: "Your home's siding is its armor against Michigan's brutal winters and humid summers. Moceri Residential Services installs premium vinyl, fiber cement, and engineered wood siding that transforms your home's curb appeal while providing lasting protection against the elements.",
    benefits: [{
      title: 'Material Variety',
      desc: 'Choose from vinyl, James Hardie fiber cement, or engineered wood to match your style and budget.',
      icon: Shield
    }, {
      title: 'Energy Efficiency',
      desc: 'Insulated siding options reduce heat loss and lower your energy bills year-round.',
      icon: Wrench
    }, {
      title: 'Low Maintenance',
      desc: 'Modern siding requires minimal upkeep — no painting, no rotting, no hassle.',
      icon: ThumbsUp
    }],
    process: ['Exterior Assessment & Measurement', 'Material & Color Selection', 'Old Siding Removal & Inspection', 'Housewrap & Insulation Installation', 'New Siding Installation & Trim Work'],
    faqs: [{
      question: 'What type of siding is best for Michigan homes?',
      answer: 'Fiber cement (James Hardie) and insulated vinyl are the most popular choices for Michigan. Both handle freeze-thaw cycles well and resist moisture damage. We help you choose based on your budget and aesthetic preferences.'
    }, {
      question: 'How long does siding installation take?',
      answer: 'A typical whole-house siding project takes 5 to 10 days depending on the size of the home and the complexity of the trim work.'
    }, {
      question: 'Will new siding increase my home value?',
      answer: 'Absolutely. New siding is one of the highest-ROI home improvements, often recouping 70-80% of the cost at resale while dramatically improving curb appeal.'
    }]
  },
  windows: {
    title: 'Energy-Efficient Window Replacement',
    image: 'https://images.unsplash.com/photo-1583608205776-bfd35f0d9f83?w=1920&q=80',
    description: "Drafty, outdated windows cost you money every month in wasted energy. Moceri Residential Services installs high-performance replacement windows that enhance your home's comfort, security, and curb appeal while significantly reducing heating and cooling costs.",
    benefits: [{
      title: 'Energy Savings',
      desc: 'Double and triple-pane Low-E glass dramatically reduces heat transfer and lowers utility bills.',
      icon: Shield
    }, {
      title: 'Custom Fit',
      desc: 'Every window is precision-measured and custom-ordered to ensure a perfect, draft-free fit.',
      icon: Wrench
    }, {
      title: 'Style Options',
      desc: 'Choose from casement, double-hung, bay, bow, slider, and picture window styles.',
      icon: ThumbsUp
    }],
    process: ['In-Home Consultation & Measurement', 'Window Style & Glass Selection', 'Custom Manufacturing (2-4 weeks)', 'Professional Installation', 'Final Inspection & Warranty Registration'],
    faqs: [{
      question: 'How much can I save on energy bills with new windows?',
      answer: 'Homeowners typically see a 15-25% reduction in heating and cooling costs after replacing old single-pane windows with modern energy-efficient units.'
    }, {
      question: 'How long do replacement windows last?',
      answer: 'Quality vinyl and fiberglass replacement windows can last 20 to 30 years or more with proper care.'
    }, {
      question: 'Do you install bay and bow windows?',
      answer: 'Yes, we install all window styles including bay, bow, casement, double-hung, slider, picture, and specialty shapes.'
    }]
  },
  painting: {
    title: 'Professional Exterior Painting',
    image: 'https://images.unsplash.com/photo-1562259949-e8e7689d7828?w=1920&q=80',
    description: "A fresh coat of paint does more than beautify your home — it protects wood, trim, and siding from moisture, UV damage, and Michigan's harsh weather cycles. Our professional exterior painting services deliver flawless results that last for years.",
    benefits: [{
      title: 'Premium Paints',
      desc: 'We use top-quality exterior paints from Sherwin-Williams and Benjamin Moore for lasting color and protection.',
      icon: Shield
    }, {
      title: 'Thorough Prep',
      desc: 'Proper surface preparation is 80% of a great paint job. We power wash, scrape, sand, prime, and caulk before any paint goes on.',
      icon: Wrench
    }, {
      title: 'Clean Results',
      desc: 'Crisp lines, even coverage, and meticulous attention to detail on every surface.',
      icon: ThumbsUp
    }],
    process: ['Color Consultation & Estimate', 'Surface Preparation & Power Washing', 'Priming & Caulking', 'Paint Application (2 coats minimum)', 'Final Touch-Ups & Inspection'],
    faqs: [{
      question: 'How often should I repaint my home exterior?',
      answer: 'In Michigan, most exterior paint jobs last 7 to 10 years depending on the quality of paint used and the amount of sun and weather exposure.'
    }, {
      question: 'What time of year is best for exterior painting?',
      answer: 'Late spring through early fall (May to October) is ideal in Michigan. We need consistent temperatures above 50°F and dry conditions for proper paint adhesion.'
    }, {
      question: 'Do you paint trim, soffits, and fascia?',
      answer: 'Yes, we paint all exterior surfaces including siding, trim, soffits, fascia, doors, shutters, and decks.'
    }]
  },
  shingles: {
    title: 'Shingle Selection Guide',
    image: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?w=1920&q=80',
    description: "Choosing the right shingle is one of the most important decisions you'll make for your roof. Moceri Residential Services helps you navigate the options — from budget-friendly 3-tab shingles to premium designer architectural shingles — so you get the perfect balance of beauty, durability, and value.",
    benefits: [{
      title: 'Expert Guidance',
      desc: 'We walk you through every option and help you choose the best shingle for your home, climate, and budget.',
      icon: Shield
    }, {
      title: 'Top Brands',
      desc: 'We carry shingles from GAF, Owens Corning, and CertainTeed — the most trusted names in roofing.',
      icon: Wrench
    }, {
      title: 'Color Matching',
      desc: 'We help coordinate your shingle color with your siding, trim, and brick for a cohesive look.',
      icon: ThumbsUp
    }],
    process: ['Roof Assessment & Budget Discussion', 'Shingle Style & Brand Comparison', 'Color Selection with Physical Samples', 'Final Quote & Scheduling', 'Professional Installation'],
    faqs: [{
      question: 'What is the difference between 3-tab and architectural shingles?',
      answer: '3-tab shingles are flat and uniform, while architectural (dimensional) shingles have a layered, textured appearance that mimics wood shake or slate. Architectural shingles are thicker, more durable, and carry longer warranties.'
    }, {
      question: 'Which shingle brand do you recommend?',
      answer: 'We are certified installers for GAF, Owens Corning, and CertainTeed. All three are excellent — the best choice depends on your budget, color preferences, and warranty requirements.'
    }, {
      question: 'How long do architectural shingles last in Michigan?',
      answer: 'Premium architectural shingles typically last 25 to 50 years in Michigan, depending on the product line and proper installation and ventilation.'
    }]
  },
  // Fallback for other services
  default: {
    title: 'Exterior Home Services',
    image: 'https://images.unsplash.com/photo-1480074568708-e7b720bb3f09?w=1920&q=80',
    description: 'Moceri Residential Services provides top-quality exterior contracting tailored to your specific needs. With over 15 years of experience in Southeast Michigan, we guarantee exceptional craftsmanship and transparent pricing.',
    benefits: [{
      title: 'Licensed & Insured',
      desc: 'Fully credentialed to protect you and your property.',
      icon: Shield
    }, {
      title: 'Local Experts',
      desc: 'We understand Michigan weather and build to withstand it.',
      icon: Wrench
    }, {
      title: 'Satisfaction Guaranteed',
      desc: "We don't consider the job done until you are completely satisfied.",
      icon: ThumbsUp
    }],
    process: ['Initial Consultation', 'Detailed Proposal', 'Project Scheduling', 'Professional Execution', 'Final Walkthrough'],
    faqs: [{
      question: 'Do you provide free estimates?',
      answer: 'Yes, all our initial inspections and estimates are completely free and carry no obligation.'
    }, {
      question: 'Are you a local company?',
      answer: 'Yes, we are locally owned and operated out of Harrison Township, serving Macomb, St. Clair, and Oakland counties.'
    }]
  }
};
export function ServicePage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const data = serviceData[slug || ''] || serviceData['default'];
  return <div className="w-full bg-white" data-id="element-882">
      {/* Hero Section */}
      <section className="relative min-h-[40vh] md:min-h-[50vh] flex items-center pt-20 pb-16 overflow-hidden" data-id="element-883">
        <div className="absolute inset-0 z-0" data-id="element-884">
          <img src={data.image} alt="" className="absolute inset-0 w-full h-full object-cover" data-id="element-885" />
          <div className="absolute inset-0 bg-brand-blue/15 z-[1]" data-id="element-886"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/30 z-[2]" data-id="element-887"></div>
        </div>

        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full" data-id="element-888">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-889">
            {/* Breadcrumbs */}
            <nav className="flex items-center text-sm text-gray-300 font-body mb-6" data-id="element-890">
              <Link to="/" className="hover:text-brand-blue transition-colors" data-id="element-891">
                Home
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-892" />
              <Link to="/services" className="hover:text-brand-blue transition-colors" data-id="element-893">
                Services
              </Link>
              <ChevronRight size={16} className="mx-2" data-id="element-894" />
              <span className="text-white font-semibold capitalize" data-id="element-895">
                {slug?.replace('-', ' ')}
              </span>
            </nav>

            <h1 className="font-heading font-black text-4xl md:text-5xl lg:text-6xl text-white leading-tight mb-6 drop-shadow-lg" data-id="element-896">
              {data.title}{' '}
              <span className="block text-brand-blue text-3xl md:text-4xl mt-2" data-id="element-897">
                in Macomb & St. Clair County
              </span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Intro & Form Section (Above Fold) */}
      <section className="py-16 bg-gray-50 border-b border-gray-200" data-id="element-898">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-899">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20" data-id="element-900">
            <div className="w-full lg:w-7/12" data-id="element-901">
              <h2 className="font-heading font-bold text-3xl text-brand-gray-dark mb-6" data-id="element-902">
                Trusted {data.title.split(' ')[1] || 'Exterior'} Experts Since
                2009
              </h2>
              <div className="w-16 h-1 bg-brand-blue mb-8 rounded-full" data-id="element-903"></div>
              <p className="font-body text-lg text-brand-gray leading-relaxed mb-8" data-id="element-904">
                {data.description}
              </p>

              <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100" data-id="element-905">
                <h3 className="font-heading font-bold text-xl text-brand-gray-dark mb-4" data-id="element-906">
                  Why Choose Moceri?
                </h3>
                <ul className="space-y-3" data-id="element-907">
                  <li className="flex items-start gap-3" data-id="element-908">
                    <CheckCircle2 className="text-brand-blue shrink-0 mt-0.5" size={20} data-id="element-909" />
                    <span className="font-body text-brand-gray" data-id="element-910">
                      Fully licensed and insured in Michigan
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-911">
                    <CheckCircle2 className="text-brand-blue shrink-0 mt-0.5" size={20} data-id="element-912" />
                    <span className="font-body text-brand-gray" data-id="element-913">
                      Upfront, transparent pricing with no hidden fees
                    </span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-914">
                    <CheckCircle2 className="text-brand-blue shrink-0 mt-0.5" size={20} data-id="element-915" />
                    <span className="font-body text-brand-gray" data-id="element-916">
                      Local family business based in Harrison Township
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            {/* CTA Form */}
            <div className="w-full lg:w-5/12 -mt-24 relative z-30" data-id="element-917">
              <QuoteForm data-id="element-918" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white" data-id="element-919">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-920">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-921">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-4" data-id="element-922">
              The Moceri Difference
            </h2>
            <div className="w-20 h-1 bg-brand-blue mx-auto mb-6 rounded-full" data-id="element-923"></div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-924">
            {data.benefits.map((benefit: any, index: number) => <div key={index} className="bg-gray-50 p-8 rounded-xl border border-gray-100 text-center hover:shadow-md transition-shadow" data-id="element-925">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100" data-id="element-926">
                  <benefit.icon size={32} className="text-brand-blue" data-id="element-927" />
                </div>
                <h3 className="font-heading font-bold text-xl text-brand-gray-dark mb-3" data-id="element-928">
                  {benefit.title}
                </h3>
                <p className="font-body text-brand-gray" data-id="element-929">{benefit.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-brand-gray-dark text-white" data-id="element-930">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-931">
          <div className="flex flex-col md:flex-row gap-12 items-center" data-id="element-932">
            <div className="w-full md:w-1/2" data-id="element-933">
              <h2 className="font-heading font-bold text-3xl md:text-4xl mb-4" data-id="element-934">
                Our Proven Process
              </h2>
              <div className="w-20 h-1 bg-brand-blue mb-8 rounded-full" data-id="element-935"></div>
              <p className="font-body text-gray-300 text-lg mb-8" data-id="element-936">
                We believe in clear communication and a streamlined workflow.
                From the first call to the final cleanup, here is what you can
                expect when working with us.
              </p>
            </div>

            <div className="w-full md:w-1/2" data-id="element-937">
              <div className="space-y-6" data-id="element-938">
                {data.process.map((step: string, index: number) => <div key={index} className="flex items-center gap-4 bg-white/5 p-4 rounded-lg border border-white/10" data-id="element-939">
                    <div className="w-10 h-10 rounded-full bg-brand-blue flex items-center justify-center font-heading font-bold text-lg shrink-0" data-id="element-940">
                      {index + 1}
                    </div>
                    <span className="font-body font-semibold text-lg" data-id="element-941">
                      {step}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50" data-id="element-942">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-943">
          <div className="text-center max-w-3xl mx-auto mb-12" data-id="element-944">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-brand-gray-dark mb-4" data-id="element-945">
              Frequently Asked Questions
            </h2>
            <div className="w-20 h-1 bg-brand-blue mx-auto mb-6 rounded-full" data-id="element-946"></div>
          </div>
          <FAQAccordion items={data.faqs} data-id="element-947" />
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20 bg-brand-blue text-center" data-id="element-948">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-949">
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-white mb-6" data-id="element-950">
            Ready to Start Your Project?
          </h2>
          <p className="font-body text-xl text-blue-100 mb-10" data-id="element-951">
            Contact Moceri Residential Services today for your free,
            no-obligation estimate.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-952">
            <a href="#quote" className="bg-white text-brand-blue font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-gray-50 transition-colors shadow-lg" data-id="element-953">
              Get a Free Quote
            </a>
            <a href="tel:5862033701" className="bg-transparent border-2 border-white text-white font-body font-bold text-lg py-4 px-10 rounded-lg hover:bg-white/10 transition-colors flex items-center justify-center gap-2" data-id="element-954">
              <Phone size={20} data-id="element-955" />
              (586) 203-3701
            </a>
          </div>
        </div>
      </section>
    </div>;
}